import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchListPost } from '~/services/post';
import { Post } from '~/types/types';

type PaginationState = {
  page: number;
  limit: number;
};

type PaginationMeta = {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  limit: number;
};

const useFetchPost = (id: string) => {
  const [pageState, setPageState] = useState<PaginationState>({
    page: 1,
    limit: 6,
  });
  const [meta, setMeta] = useState<PaginationMeta>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 6,
  });
  const [data, setData] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPost = useCallback(
    async (page: number, limit: number) => {
      setIsLoading(true);
      try {
        const response = await fetchListPost(id, {
          limit,
          page,
        });
        setData(response.data);
        setMeta({
          currentPage: response.pagination.currentPage,
          totalPages: response.pagination.totalPages,
          totalRecords: response.pagination.totalRecords,
          limit: response.pagination.limit,
        });
        setPageState((prev) => {
          const next = {
            page: response.pagination.currentPage,
            limit: response.pagination.limit,
          };
          if (prev.page === next.page && prev.limit === next.limit) {
            return prev;
          }
          return next;
        });
      } finally {
        setIsLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchPost(pageState.page, pageState.limit);
  }, [fetchPost, pageState.page, pageState.limit]);

  const handlePageChange = useCallback((page: number) => {
    setPageState((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((limit: number) => {
    setPageState({ page: 1, limit });
  }, []);

  const hasNextPage = useMemo(() => meta.currentPage < meta.totalPages, [meta]);
  const hasPreviousPage = useMemo(() => meta.currentPage > 1, [meta]);

  return {
    data,
    isLoading,
    pagination: {
      currentPage: meta.currentPage,
      totalPages: meta.totalPages,
      totalRecords: meta.totalRecords,
      pageSize: meta.limit,
      hasNextPage,
      hasPreviousPage,
    },
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
  };
};

export default useFetchPost;
